import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Card, CardContent, Grid, Typography, Chip, Divider, Button } from "@mui/material";
import { toast } from "react-toastify";

import api from "./../api";
import { MONO, tokens } from "./../theme";
import { PageHeader, Metric, Eyebrow, StatusDot } from "./../components/ui";
import VramGauge from "./../components/VramGauge";

const fmt = (n) => Math.round(n || 0).toLocaleString();
const utilColor = (u) => (u >= 90 ? tokens.crit : u >= 60 ? tokens.warn : tokens.ok);

function StatRail({ ov }) {
  if (!ov) return null;
  const cells = [
    { label: "Total VRAM", value: fmt(ov.gpus.total_mb), unit: "MB" },
    { label: "In use", value: fmt(ov.gpus.used_mb), unit: "MB", accent: tokens.signal },
    { label: "Reserved", value: fmt(ov.gpus.reserved_mb), unit: "MB" },
    { label: "Free", value: fmt(ov.gpus.free_mb), unit: "MB", accent: tokens.ok },
    { label: "Running", value: ov.queue.running },
    { label: "Queued", value: ov.queue.pending, accent: ov.queue.pending ? tokens.warn : undefined },
    ...(ov.gpus.workers_online
      ? [{ label: "Workers", value: ov.gpus.workers_online, accent: tokens.signal }]
      : []),
  ];
  return (
    <Card sx={{ mb: 2.5 }}>
      <CardContent sx={{ py: 2 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)", md: "repeat(auto-fit, minmax(90px, 1fr))" },
            gap: 2.5,
          }}
        >
          {cells.map((c, i) => (
            <Box key={c.label} sx={{ pl: { md: i === 0 ? 0 : 2 }, borderLeft: { md: i === 0 ? "none" : `1px solid ${tokens.line}` } }}>
              <Metric {...c} />
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
}

function TeleCell({ label, value, unit, color }) {
  return (
    <Box>
      <Eyebrow sx={{ mb: 0.4 }}>{label}</Eyebrow>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.4 }}>
        <Typography sx={{ fontFamily: MONO, fontWeight: 600, fontSize: "1.02rem", lineHeight: 1, color: color || "text.primary" }}>
          {value}
        </Typography>
        {unit && <Typography sx={{ fontFamily: MONO, fontSize: ".7rem", color: "text.secondary" }}>{unit}</Typography>}
      </Box>
    </Box>
  );
}

function GpuCard({ g, onUnload }) {
  const running = g.running || [];
  return (
    <Card sx={{ height: "100%", "&:hover": { boxShadow: "0 6px 20px rgba(14,27,46,.08)", borderColor: "#d3ddec" } }}>
      <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1.75 }}>
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <StatusDot color={utilColor(g.util)} pulse size={9} />
          <Typography sx={{ fontFamily: MONO, fontWeight: 600, fontSize: ".95rem" }}>
            GPU&nbsp;{g.local_index ?? g.index}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {g.name}
          </Typography>
          {g.mock && <Chip label="mock" size="small" sx={{ ml: "auto", bgcolor: "#eef2f8", color: tokens.slate }} />}
        </Box>

        <VramGauge usedMb={g.used_mb} reservedMb={g.reserved_mb} totalMb={g.total_mb} />

        <Divider sx={{ borderColor: tokens.line }} />

        {/* Telemetry */}
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1.5 }}>
          <TeleCell label="Util" value={g.util} unit="%" color={utilColor(g.util)} />
          <TeleCell label="Temp" value={g.temp} unit="°C" />
          <TeleCell label="Power" value={g.power_w} unit={`/${g.power_limit_w} W`} />
        </Box>

        {/* Running jobs */}
        <Box>
          <Eyebrow sx={{ mb: 0.75 }}>Running · {running.length}</Eyebrow>
          {running.length ? (
            <Box sx={{ display: "flex", gap: 0.6, flexWrap: "wrap" }}>
              {running.map((j) => (
                <Chip
                  key={j.id}
                  label={`${j.generator} · ${fmt(j.vram_mb)}MB`}
                  size="small"
                  variant="outlined"
                  sx={{ fontFamily: MONO, fontSize: ".68rem" }}
                />
              ))}
            </Box>
          ) : (
            <Typography sx={{ fontFamily: MONO, fontSize: ".78rem", color: "#9aa8bf" }}>idle</Typography>
          )}
        </Box>

        {/* vLLM server resident on this GPU */}
        {g.vllm && (
          <>
            <Divider sx={{ borderColor: tokens.line }} />
            <Box>
              <Eyebrow sx={{ mb: 0.75 }}>Serving model</Eyebrow>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                <Chip label={`▣ ${g.vllm.model}`}
                  sx={{ bgcolor: "rgba(46,139,230,.12)", color: tokens.signal, fontWeight: 600 }} />
                <Chip size="small" variant="outlined"
                  label={g.vllm.status}
                  sx={{ color: g.vllm.status === "running" ? tokens.ok : tokens.warn }} />
              </Box>
            </Box>
          </>
        )}

        {/* Hot-loaded (warm) model — kept resident so repeat jobs skip the reload */}
        {g.warm && (
          <>
            <Divider sx={{ borderColor: tokens.line }} />
            <Box>
              <Eyebrow sx={{ mb: 0.75 }}>Hot-loaded model</Eyebrow>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                <Chip
                  label={`⚡ ${g.warm.generator}`}
                  size="small"
                  sx={{ fontFamily: MONO, fontSize: ".7rem", bgcolor: "rgba(46,139,230,.12)", color: tokens.signal, fontWeight: 600 }}
                />
                {(() => {
                  // Offloaded models drop out of VRAM into system RAM between jobs;
                  // during a run they stream through VRAM. Non-offload models stay
                  // resident in VRAM.
                  const inRam = g.warm.cpu_offload && !g.warm.busy;
                  return (
                    <Chip
                      label={inRam ? "in RAM" : "in VRAM"}
                      size="small"
                      variant="outlined"
                      title={inRam
                        ? "CPU-offloaded: weights are in system RAM; the VRAM shown is reserved headroom for its next run."
                        : "Resident in VRAM."}
                      sx={{ fontFamily: MONO, fontSize: ".64rem", height: 20, fontWeight: 600,
                            color: inRam ? tokens.slate : tokens.signal,
                            borderColor: inRam ? tokens.line : "rgba(46,139,230,.4)" }}
                    />
                  );
                })()}
                <Typography sx={{ fontFamily: MONO, fontSize: ".72rem", color: "text.secondary" }}>
                  {g.warm.busy ? "running" : `idle ${Math.round(g.warm.idle_s)}s`} · {g.warm.jobs} job{g.warm.jobs === 1 ? "" : "s"}
                </Typography>
                <Button
                  size="small"
                  variant="text"
                  disabled={g.warm.busy}
                  onClick={() => onUnload(g.index)}
                  sx={{ ml: "auto", minWidth: 0, fontSize: ".7rem" }}
                >
                  Unload
                </Button>
              </Box>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function NodeHeader({ grp, online }) {
  const isLocal = grp.node === "local";
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.25, mt: 0.5 }}>
      <StatusDot color={online ? tokens.ok : tokens.slate} size={9} pulse={online} />
      <Typography sx={{ fontFamily: MONO, fontWeight: 700, fontSize: ".85rem", color: "#0e1b2e" }}>
        {grp.label}
      </Typography>
      <Chip
        size="small"
        label={isLocal ? "control center" : "worker"}
        sx={{ height: 18, fontSize: ".6rem", fontFamily: MONO, letterSpacing: ".04em",
              bgcolor: isLocal ? "rgba(46,139,230,.1)" : "#eef2f8",
              color: isLocal ? tokens.signal : tokens.slate }}
      />
      <Typography variant="caption" color="text.secondary">
        {grp.gpus.length} GPU{grp.gpus.length === 1 ? "" : "s"}
      </Typography>
    </Box>
  );
}

export default function Gpus() {
  const [gpus, setGpus] = useState([]);
  const [nodes, setNodes] = useState([]);
  const [ov, setOv] = useState(null);

  const load = useCallback(() => {
    api.get("/api/gpus", { silent: true })
      .then((d) => { setGpus(d.gpus); setNodes(d.nodes || []); })
      .catch(() => {});
    api.get("/api/overview", { silent: true }).then(setOv).catch(() => {});
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(load, 2000);
    return () => clearInterval(t);
  }, [load]);

  const unload = (idx) => {
    api.post(`/api/gpus/${idx}/unload`, {}, { silent: true })
      .then((r) => {
        toast.info(r?.reason === "requested"
          ? "Unload requested — the worker will free it shortly"
          : `Unloaded the warm model on GPU ${idx}`);
        load();
      })
      .catch((e) => toast.error(e?.detail || "Unload failed"));
  };

  // Group GPUs by node — the control center ("This host") first, then each
  // connected worker. Headers only appear once a worker is present, so a
  // single-host rack stays a clean flat grid.
  const groups = useMemo(() => {
    const m = new Map();
    for (const g of gpus) {
      const key = g.node || "local";
      if (!m.has(key)) {
        m.set(key, { node: key, label: g.node_label || (key === "local" ? "This host" : key), gpus: [] });
      }
      m.get(key).gpus.push(g);
    }
    return [...m.values()].sort((a, b) =>
      a.node === "local" ? -1 : b.node === "local" ? 1 : a.label.localeCompare(b.label));
  }, [gpus]);
  const hasRemote = groups.some((grp) => grp.node !== "local");
  const nodeOnline = useMemo(
    () => Object.fromEntries((nodes || []).map((n) => [n.node_id, n.online])), [nodes]);
  const nodeHost = useMemo(
    () => Object.fromEntries((nodes || []).filter((n) => n.host).map((n) => [n.node_id, n.host])), [nodes]);
  const hostFor = (node) => (node === "local" ? ov?.host : nodeHost[node]);
  const offlineNodes = (nodes || []).filter((n) => !n.online);

  return (
    <Box>
      <PageHeader
        eyebrow="Monitor"
        title="GPUs"
        subtitle="Live telemetry and VRAM headroom across the rack, refreshed every 2 seconds."
      />

      <StatRail ov={ov} />

      {/* Single-host: one System RAM card (with the offload explainer). With
          workers connected, RAM is shown per node inside each section below. */}
      {!hasRemote && ov?.host?.mem_total_mb > 0 && (
        <Card sx={{ mb: 2.5 }}>
          <CardContent sx={{ py: 2.25 }}>
            <VramGauge label="System RAM" usedMb={ov.host.mem_used_mb} totalMb={ov.host.mem_total_mb} />
            <Typography sx={{ mt: 1.25, fontSize: ".72rem", color: "text.secondary", lineHeight: 1.5 }}>
              Warm generators that use CPU offload keep their weights here — a model drops out of VRAM back into
              system RAM after each run, so this reflects what's actually hot-loaded between jobs, while the VRAM
              above stays <em>reserved</em> as headroom for its next run.
            </Typography>
          </CardContent>
        </Card>
      )}

      {gpus.length ? (
        hasRemote ? (
          groups.map((grp) => {
            const host = hostFor(grp.node);
            return (
            <Box key={grp.node} sx={{ mb: 2.5 }}>
              <NodeHeader grp={grp} online={grp.node === "local" || nodeOnline[grp.node]} />
              {host?.mem_total_mb > 0 && (
                <Card sx={{ mb: 1.5 }}>
                  <CardContent sx={{ py: 1.5 }}>
                    <VramGauge label="System RAM" usedMb={host.mem_used_mb} totalMb={host.mem_total_mb} />
                  </CardContent>
                </Card>
              )}
              <Grid container spacing={2}>
                {grp.gpus.map((g) => (
                  <Grid item xs={12} sm={6} lg={4} key={g.index}>
                    <GpuCard g={g} onUnload={unload} />
                  </Grid>
                ))}
              </Grid>
            </Box>
          ); })
        ) : (
          <Grid container spacing={2}>
            {gpus.map((g) => (
              <Grid item xs={12} sm={6} lg={4} key={g.index}>
                <GpuCard g={g} onUnload={unload} />
              </Grid>
            ))}
          </Grid>
        )
      ) : (
        <Card>
          <CardContent sx={{ py: 5, textAlign: "center" }}>
            <Typography sx={{ fontWeight: 600, mb: 0.5 }}>No GPUs detected</Typography>
            <Typography variant="body2" color="text.secondary">
              Run on a GPU host with the NVIDIA container runtime, or set <code style={{ fontFamily: MONO }}>GEN_MOCK_GPUS</code> for a dev rack.
            </Typography>
          </CardContent>
        </Card>
      )}

      {offlineNodes.length > 0 && (
        <Card sx={{ mt: 2 }}>
          <CardContent sx={{ py: 1.5, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <StatusDot color={tokens.slate} size={8} />
            <Typography variant="body2" color="text.secondary">
              Offline worker{offlineNodes.length > 1 ? "s" : ""}:
            </Typography>
            {offlineNodes.map((n) => (
              <Chip
                key={n.node_id}
                size="small"
                label={`${n.label} · last seen ${Math.round(n.last_seen_s)}s ago`}
                sx={{ fontFamily: MONO, fontSize: ".68rem", bgcolor: "#f4f6fa", color: tokens.slate }}
              />
            ))}
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
